import { Icon } from "@circle-react-shared/Icon";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { useIsOrCanBeCommunityMember } from "@circle-react/contexts/punditUserContext";
import { isPostDetailsPage } from "@circle-react/helpers/isPostDetailsPage";

import { t } from "@/i18n-js/instance";
import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { useNavigateToPost } from "../PostModal/hooks/useNavigateToPost";
import { usePostNavigationContext } from "../PostViewList/PostNavigationContext";
import { CommentCountLink } from "./CommentCountLink";
import { PostLikedByMemberStackV3 } from "./PostLikedByMemberStackV3";

interface CommentsFormProps {
  shouldForceSamePageInteractions?: boolean;
  shouldOpenCommentPage: boolean;
  showCommentAndFocusCommentForm: () => void;
  toggleComment: () => void;
}

export const CommentsForm = ({
  shouldOpenCommentPage,
  showCommentAndFocusCommentForm,
  toggleComment,
  shouldForceSamePageInteractions = false,
}: CommentsFormProps) => {
  const isOrCanBeCommunityMember = useIsOrCanBeCommunityMember();
  const { navigateToPost } = useNavigateToPost();
  const { params, hasNextPage } = usePostNavigationContext();

  const { post } = useCurrentPostHelpers();
  const {
    is_comments_closed: isCommentsClosed,
    is_comments_enabled: isCommentsEnabled,
    comments_count: commentsCount = 0,
    post_details_loaded,
  } = post;

  const shouldShowPostModal = !isPostDetailsPage();

  const isCommentsForm = isCommentsEnabled && !shouldOpenCommentPage;

  if (!isCommentsForm) {
    return (
      <div
        className={classNames(
          anchorClassNames("engagement__comments"),
          "ml-auto mr-0 flex items-center gap-2",
        )}
      >
        <PostLikedByMemberStackV3 shouldDisplayVerticalDivider={false} />
      </div>
    );
  }

  return (
    <>
      {!isCommentsClosed && isOrCanBeCommunityMember && (
        <button
          type="button"
          onClick={() => {
            shouldShowPostModal
              ? navigateToPost({
                  newPost: post,
                  params,
                  hasNextPage,
                })
              : showCommentAndFocusCommentForm();
          }}
          aria-label={t("cap_comment_aria_label", {
            title: post?.name ?? t("this_post"),
          })}
          className={classNames(
            anchorClassNames("action-comment"),
            "flex items-center px-1 py-0.5 text-sm font-medium text-default transition-colors duration-150 ease-in-out hover:!text-dark focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
          )}
          data-testid="post-comment-button"
        >
          <span className={anchorClassNames("action__icon")}>
            <Icon type="24-comment" size={24} />
          </span>
        </button>
      )}
      <div
        className={classNames(
          anchorClassNames("engagement__comments"),
          "ml-auto mr-0 flex items-center gap-2",
        )}
        data-testid="engagement-comments"
      >
        <PostLikedByMemberStackV3 />
        <CommentCountLink
          count={commentsCount}
          toggleComment={toggleComment}
          hasPostDetailsLoaded={post_details_loaded}
          shouldForceSamePageInteractions={shouldForceSamePageInteractions}
        />
      </div>
    </>
  );
};
